import { useState, useEffect } from 'react';

/**
 * useIsMobile
 * Returns true when the viewport is at or below `breakpoint` px (default 768).
 *
 * The codebase styles almost everything with inline React styles, which can't
 * hold CSS media queries — this hook lets components branch those inline styles
 * on screen size instead. Tracks viewport changes (resize / orientation) live.
 */
export default function useIsMobile(breakpoint = 768) {
  const query = `(max-width: ${breakpoint}px)`;

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);

    // Sync immediately in case the breakpoint changed between renders
    setIsMobile(mql.matches);

    // addEventListener is the modern API; addListener is the Safari < 14 fallback
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return isMobile;
}
